import { escapeHtml } from './security';
// Graph and timeline snapshots for report documents (PLAN §10.4: "embedding graph/timeline snapshots"; task 36).
// Pure functions that draw a report's results as standalone SVG, always in the light print palette (a printed page is
// white whatever theme the exporter uses). The browser rasterizes the SVG to PNG and sends it with the export, so
// the same picture goes into the PDF and the DOCX. Colors come from the design tokens only; labels keep their own
// direction, so Arabic and mixed labels read correctly.

import { entityFallback, entityPalette, gray, semantic } from './design/tokens';
import { textDirection, truncateLabel } from './graph';
import { itemSpan, packRows, type TimelineItem, timelineTicks, timelineWindow } from './timeline';

export const SNAPSHOT_KINDS = ['graph', 'timeline'] as const;
export type SnapshotKind = (typeof SNAPSHOT_KINDS)[number];
export const SNAPSHOT_LABELS: Record<SnapshotKind, string> = { graph: 'Graph', timeline: 'Timeline' };

/** What one export may carry: at most one picture of each kind, each a PNG under 3 MB and 6000 px a side. */
export const SNAPSHOT_LIMITS = { count: SNAPSHOT_KINDS.length, bytes: 3_000_000, side: 6000, nodes: 60, lanes: 12, items: 300 };

/** Print palette: the light-mode tokens. */
export const PRINT_COLORS = {
  background: semantic.surface.light,
  foreground: semantic.foreground.light,
  muted: semantic['muted-foreground'].light,
  line: gray[400],
  grid: gray[200],
  entity: (slot: number | null | undefined) =>
    (slot && slot >= 1 && slot <= entityPalette.length ? entityPalette[slot - 1]?.light : null) ?? entityFallback.light,
};

const FONT = '"Noto Sans", "Noto Naskh Arabic", "Geeza Pro", Arial, sans-serif';
const esc = escapeHtml;
const n1 = (v: number) => v.toFixed(1);

/** A label as SVG text in its own direction, anchored so it starts at `x` either way. */
function label(
  text: string,
  x: number,
  y: number,
  opts: { size?: number; weight?: number; fill?: string; max?: number; halo?: boolean } = {},
) {
  const t = truncateLabel(text, opts.max ?? 28);
  const dir = textDirection(t);
  return `<text x="${n1(x)}" y="${n1(y)}" direction="${dir}" text-anchor="${dir === 'rtl' ? 'end' : 'start'}" unicode-bidi="plaintext" dominant-baseline="middle" font-size="${opts.size ?? 11}"${
    opts.weight ? ` font-weight="${opts.weight}"` : ''
  }${opts.halo ? ` paint-order="stroke" stroke="${PRINT_COLORS.background}" stroke-width="3" stroke-linejoin="round"` : ''} fill="${opts.fill ?? PRINT_COLORS.foreground}">${esc(t)}</text>`;
}

const frame = (w: number, h: number, body: string, title: string) =>
  [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" font-family="${esc(FONT)}" role="img" aria-label="${esc(title)}">`,
    `<title>${esc(title)}</title>`,
    `<rect width="100%" height="100%" fill="${PRINT_COLORS.background}"/>`,
    body,
    '</svg>',
  ].join('\n');

// ─── Graph ───────────────────────────────────────────────────────────────────

export interface SnapshotNode {
  id: string;
  label: string;
  slot: number | null;
}
export interface SnapshotEdge {
  source: string;
  target: string;
  label: string;
  directed: boolean;
}

/**
 * Deterministic force-directed layout (Fruchterman–Reingold from a circle, fixed iterations): the same results always
 * give the same picture. Positions are in the unit square.
 */
export function layoutGraph(nodes: readonly { id: string }[], edges: readonly { source: string; target: string }[], iterations = 300) {
  const n = nodes.length;
  const pos = nodes.map((_, i) => ({
    x: 0.5 + 0.4 * Math.cos((2 * Math.PI * i) / Math.max(1, n)),
    y: 0.5 + 0.4 * Math.sin((2 * Math.PI * i) / Math.max(1, n)),
  }));
  if (n < 3) return new Map(nodes.map((nd, i) => [nd.id, n === 1 ? { x: 0.5, y: 0.5 } : (pos[i] as { x: number; y: number })]));
  const index = new Map(nodes.map((nd, i) => [nd.id, i]));
  const links = edges
    .map(e => [index.get(e.source), index.get(e.target)] as const)
    .filter((l): l is readonly [number, number] => l[0] !== undefined && l[1] !== undefined && l[0] !== l[1]);
  const k = Math.sqrt(1 / n);
  let temp = 0.1;
  for (let it = 0; it < iterations; it++) {
    const disp = pos.map(() => ({ x: 0, y: 0 }));
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++) {
        const a = pos[i] as { x: number; y: number };
        const b = pos[j] as { x: number; y: number };
        const dx = a.x - b.x || 1e-6 * (i - j);
        const dy = a.y - b.y;
        const d = Math.max(1e-4, Math.hypot(dx, dy));
        const f = (k * k) / d;
        const di = disp[i] as { x: number; y: number };
        const dj = disp[j] as { x: number; y: number };
        di.x += (dx / d) * f;
        di.y += (dy / d) * f;
        dj.x -= (dx / d) * f;
        dj.y -= (dy / d) * f;
      }
    for (const [i, j] of links) {
      const a = pos[i] as { x: number; y: number };
      const b = pos[j] as { x: number; y: number };
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const d = Math.max(1e-4, Math.hypot(dx, dy));
      const f = (d * d) / k;
      const di = disp[i] as { x: number; y: number };
      const dj = disp[j] as { x: number; y: number };
      di.x -= (dx / d) * f;
      di.y -= (dy / d) * f;
      dj.x += (dx / d) * f;
      dj.y += (dy / d) * f;
    }
    for (let i = 0; i < n; i++) {
      const p = pos[i] as { x: number; y: number };
      const d = disp[i] as { x: number; y: number };
      // Gentle pull to the centre keeps unlinked entities from drifting to the edges.
      d.x += (0.5 - p.x) * 0.05;
      d.y += (0.5 - p.y) * 0.05;
      const len = Math.max(1e-6, Math.hypot(d.x, d.y));
      p.x += (d.x / len) * Math.min(len, temp);
      p.y += (d.y / len) * Math.min(len, temp);
    }
    temp = Math.max(0.002, temp * 0.985);
  }
  // Fit to the unit square.
  const xs = pos.map(p => p.x);
  const ys = pos.map(p => p.y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const sx = maxX - minX || 1;
  const sy = maxY - minY || 1;
  return new Map(nodes.map((nd, i) => [nd.id, { x: ((pos[i]?.x ?? 0) - minX) / sx, y: ((pos[i]?.y ?? 0) - minY) / sy }]));
}

/** The results and the links among them, as a picture: entity dots in their "Color by" color, labelled. */
export function graphSnapshotSvg(
  input: { nodes: readonly SnapshotNode[]; edges: readonly SnapshotEdge[] },
  opts: { width?: number; height?: number } = {},
): string {
  const w = opts.width ?? 960;
  const h = opts.height ?? 600;
  const nodes = input.nodes.slice(0, SNAPSHOT_LIMITS.nodes);
  const ids = new Set(nodes.map(n => n.id));
  const edges = input.edges.filter(e => ids.has(e.source) && ids.has(e.target) && e.source !== e.target);
  const layout = layoutGraph(nodes, edges);
  const padX = 150;
  const padY = 36;
  const at = (id: string) => {
    const p = layout.get(id) ?? { x: 0.5, y: 0.5 };
    return { x: padX + p.x * (w - 2 * padX), y: padY + p.y * (h - 2 * padY) };
  };
  const r = nodes.length > 30 ? 6 : 8;
  const showEdgeLabels = edges.length <= 16;
  const lines = edges
    .map(e => {
      const a = at(e.source);
      const b = at(e.target);
      const line = `<line x1="${n1(a.x)}" y1="${n1(a.y)}" x2="${n1(b.x)}" y2="${n1(b.y)}" stroke="${PRINT_COLORS.line}" stroke-width="1.2"${
        e.directed ? ' marker-end="url(#arrow)"' : ''
      }/>`;
      if (!showEdgeLabels || !e.label) return line;
      return `${line}${label(e.label, (a.x + b.x) / 2, (a.y + b.y) / 2 - 7, { size: 9, fill: PRINT_COLORS.muted, max: 24, halo: true })}`;
    })
    .join('');
  const dots = nodes
    .map(nd => {
      const p = at(nd.id);
      return `<circle cx="${n1(p.x)}" cy="${n1(p.y)}" r="${r}" fill="${PRINT_COLORS.entity(nd.slot)}" stroke="${PRINT_COLORS.foreground}" stroke-opacity="0.55" stroke-width="1"/>${label(
        nd.label,
        p.x + r + 4,
        p.y,
        { size: 11, halo: true },
      )}`;
    })
    .join('');
  const empty = nodes.length ? '' : label('No entities to draw.', 20, h / 2, { fill: PRINT_COLORS.muted });
  return frame(
    w,
    h,
    `<defs><marker id="arrow" viewBox="0 0 10 10" refX="${r + 9}" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 10 5 0 10Z" fill="${PRINT_COLORS.line}"/></marker></defs><g>${lines}</g><g>${dots}</g>${empty}`,
    `Graph of ${nodes.length} entities and ${edges.length} links`,
  );
}

// ─── Timeline ────────────────────────────────────────────────────────────────

export interface SnapshotLane {
  id: string;
  label: string;
  slot: number | null;
}
export interface SnapshotItem extends Pick<TimelineItem, 'lane' | 'kind' | 'from' | 'to' | 'openEnd'> {
  label: string;
  slot: number | null;
}

/** One swimlane per entity, time left to right: periods as bars, events and entries as marks, each labelled. */
export function timelineSnapshotSvg(
  input: { lanes: readonly SnapshotLane[]; items: readonly SnapshotItem[] },
  opts: { width?: number; now?: number } = {},
): string {
  const w = opts.width ?? 960;
  const now = opts.now ?? Date.now();
  const lanes = input.lanes.slice(0, SNAPSHOT_LIMITS.lanes);
  const laneIds = new Set(lanes.map(l => l.id));
  const items = input.items.filter(i => laneIds.has(i.lane) && itemSpan(i, now)).slice(0, SNAPSHOT_LIMITS.items);
  const labelW = 170;
  const trackX = labelW + 10;
  const trackW = w - trackX - 20;
  const axisH = 26;
  const rowH = 20;
  const window = timelineWindow(items, {}, now);
  if (!window || !lanes.length)
    return frame(w, 80, label('Nothing dated to draw.', 20, 40, { fill: PRINT_COLORS.muted }), 'Timeline with no dated items');
  const x = (t: number) => trackX + ((t - window.start) / (window.end - window.start || 1)) * trackW;
  const LABEL_PX = 130;
  const laneRows = lanes.map(l => {
    const mine = items
      .filter(i => i.lane === l.id)
      .map(i => {
        const s = itemSpan(i, now) as { start: number; end: number };
        const x0 = x(s.start);
        const x1 = i.kind === 'period' ? Math.max(x0 + 3, x(s.end)) : x0;
        const flip = i.kind !== 'period' && x0 > trackX + trackW - LABEL_PX;
        return { i, x0, x1, flip };
      })
      .sort((a, b) => a.x0 - b.x0);
    const rows = packRows(
      mine.map(m => (m.flip ? { start: m.x0 - LABEL_PX, end: m.x0 } : { start: m.x0, end: Math.max(m.x1, m.x0 + LABEL_PX) })),
      6,
    );
    return { lane: l, placed: mine.map((m, k) => ({ ...m, row: rows[k] ?? 0 })), rows: Math.max(1, ...rows.map(r => r + 1)) };
  });
  const h = axisH + laneRows.reduce((s, l) => s + l.rows * rowH + 10, 0) + 10;
  const ticks = timelineTicks(window, 10)
    .map(
      t =>
        `<line x1="${n1(x(t.at))}" y1="${axisH - 4}" x2="${n1(x(t.at))}" y2="${h - 6}" stroke="${PRINT_COLORS.grid}"/><text x="${n1(x(t.at))}" y="${axisH - 10}" text-anchor="middle" font-size="10" fill="${PRINT_COLORS.muted}">${esc(t.label)}</text>`,
    )
    .join('');
  let y = axisH;
  const body = laneRows
    .map(({ lane, placed, rows }) => {
      const top = y;
      y += rows * rowH + 10;
      const marks = placed
        .map(p => {
          const cy = top + 5 + p.row * rowH + rowH / 2;
          const fill = PRINT_COLORS.entity(p.i.slot);
          const outline = `stroke="${PRINT_COLORS.foreground}" stroke-opacity="0.55" stroke-width="1"`;
          const mark =
            p.i.kind === 'period'
              ? `<rect x="${n1(p.x0)}" y="${n1(cy - 5)}" width="${n1(p.x1 - p.x0)}" height="10" rx="2" fill="${fill}" ${outline}/>`
              : p.i.kind === 'event'
                ? `<path d="M${n1(p.x0)} ${n1(cy - 6)}l6 6-6 6-6-6z" fill="${fill}" ${outline}/>`
                : `<rect x="${n1(p.x0 - 4)}" y="${n1(cy - 4)}" width="8" height="8" fill="${fill}" ${outline}/>`;
          const text = p.flip
            ? label(p.i.label, p.x0 - 9 - LABEL_PX + 12, cy, { size: 9.5, max: 22, halo: true })
            : label(p.i.label, (p.i.kind === 'period' ? p.x0 : p.x0 + 8) + 2, p.i.kind === 'period' ? cy - 10 : cy, {
                size: 9.5,
                max: 22,
                halo: true,
              });
          return mark + text;
        })
        .join('');
      return `<line x1="0" y1="${top}" x2="${w}" y2="${top}" stroke="${PRINT_COLORS.grid}"/><circle cx="10" cy="${top + 5 + rowH / 2}" r="5" fill="${PRINT_COLORS.entity(
        lane.slot,
      )}"/>${label(lane.label, 20, top + 5 + rowH / 2, { size: 11, weight: 600, max: 22 })}${marks}`;
    })
    .join('');
  return frame(w, Math.ceil(h), ticks + body, `Timeline of ${lanes.length} entities with ${items.length} dated items`);
}

// ─── PNG checks (the server side of a snapshot) ──────────────────────────────

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/** Width and height of a PNG from its IHDR chunk, or null when the bytes are not a PNG. */
export function pngSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 24 || PNG_SIGNATURE.some((b, i) => bytes[i] !== b)) return null;
  if (String.fromCharCode(...bytes.slice(12, 16)) !== 'IHDR') return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { width: v.getUint32(16), height: v.getUint32(20) };
}

export interface Snapshot {
  kind: SnapshotKind;
  caption: string;
  png: Uint8Array;
  width: number;
  height: number;
}

export const base64Bytes = (s: string): Uint8Array | null => {
  try {
    const bin = atob(s.replace(/^data:image\/png;base64,/, ''));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
};

/**
 * Snapshots sent with an export: `[{ kind: 'graph' | 'timeline', caption?, png: <base64> }]`. Only real PNGs within
 * the limits pass; the picture is the exporter's own view of the results, embedded as an image and never read.
 */
export function parseSnapshots(v: unknown): { ok: true; value: Snapshot[] } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: [] };
  if (!Array.isArray(v)) return { ok: false, error: 'Snapshots must be a list.' };
  if (v.length > SNAPSHOT_LIMITS.count) return { ok: false, error: `Add at most ${SNAPSHOT_LIMITS.count} snapshots.` };
  const out: Snapshot[] = [];
  for (const s of v) {
    const o = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>;
    const kind = o.kind as SnapshotKind;
    if (!SNAPSHOT_KINDS.includes(kind)) return { ok: false, error: 'A snapshot is a graph or a timeline.' };
    if (out.some(x => x.kind === kind)) return { ok: false, error: `Only one ${kind} snapshot per export.` };
    if (typeof o.png !== 'string' || o.png.length > (SNAPSHOT_LIMITS.bytes * 4) / 3 + 64)
      return { ok: false, error: `The ${kind} snapshot is missing or larger than 3 MB.` };
    const png = base64Bytes(o.png);
    const size = png && pngSize(png);
    if (!png || !size) return { ok: false, error: `The ${kind} snapshot is not a PNG image.` };
    if (!size.width || !size.height || size.width > SNAPSHOT_LIMITS.side || size.height > SNAPSHOT_LIMITS.side)
      return { ok: false, error: `The ${kind} snapshot is too large.` };
    const caption = typeof o.caption === 'string' ? o.caption.trim().replace(/\s+/g, ' ').slice(0, 200) : '';
    out.push({ kind, caption: caption || SNAPSHOT_LABELS[kind], png, ...size });
  }
  return { ok: true, value: out };
}
